// src/pages/partners/partners-logged-in/routes/vault/RecipesVaultList.tsx
import React, {
  useState,
  useCallback,
  useEffect,
  useRef,
  KeyboardEvent,
} from "react";
import {
  Box,
  Stack,
  Typography,
  Chip,
  Button,
  Drawer,
  TextField,
  IconButton,
  Paper,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Autocomplete,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import Alert from "@mui/material/Alert";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ViewListIcon from "@mui/icons-material/ViewList";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import GlassContainer from "../../../components/liquid-glass";
import VaultGridCard from "./VaultGridCard";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "../../../../../redux/store";
import { Recipe, Ingredient } from "../../../../../models/types";
import {
  upsertRecipe,
  deleteRecipeThunk as deleteRecipe,
} from "../../../../../redux/thunks/thunk-recipes";

export interface RecipesVaultListProps {
  recipes: Recipe[];
  onCreateOrUpdateRecipe: (r: Recipe) => void;
  onDeleteRecipe: (recipeId: string) => void;
}

const uid = () => Math.random().toString(36).slice(2) + Date.now().toString(36);

const emptyNutrition = () => ({
  calories: 0,
  protein: 0,
  carbs: 0,
  fats: 0,
});

const emptyIngredient = (): Ingredient => ({
  name: "",
  quantity: 0,
  unit: "g",
  notes: "",
  nutritionPerUnit: emptyNutrition(),
});

type SortKey = "title";

// Generate pastel colors for tags/labels based on the string
function getTagColors(tag: string): {
  backgroundColor: string;
  color: string;
  borderColor: string;
} {
  let hash = 0;
  for (let i = 0; i < tag.length; i++) {
    hash = (hash * 31 + tag.charCodeAt(i)) | 0;
  }
  const hue = Math.abs(hash) % 360;
  const backgroundColor = `hsl(${hue}, 70%, 88%)`; // light pastel
  const borderColor = `hsl(${hue}, 60%, 60%)`; // mid tone
  const color = `hsl(${hue}, 70%, 25%)`; // darker text
  return { backgroundColor, color, borderColor };
}

const normalize = (s?: string | null) =>
  (s ?? "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "");

/**
 * Compute macros for the recipe.
 * Entire recipe = 1 serving.
 * We DO NOT multiply by quantity. We just add what the user typed for each ingredient.
 */
function computeTotals(ingredients: Ingredient[]) {
  let totalProtein = 0;
  let totalCarbs = 0;
  let totalFats = 0;

  ingredients.forEach((ing) => {
    const p = ing.nutritionPerUnit?.protein ?? 0;
    const c = ing.nutritionPerUnit?.carbs ?? 0;
    const f = ing.nutritionPerUnit?.fats ?? 0;

    totalProtein += p;
    totalCarbs += c;
    totalFats += f;
  });

  const totalCalories = totalProtein * 4 + totalCarbs * 4 + totalFats * 9;

  const totals = {
    calories: Math.round(totalCalories),
    protein: Math.round(totalProtein),
    carbs: Math.round(totalCarbs),
    fats: Math.round(totalFats),
  };

  return {
    totalMacros: totals,
    perServingMacros: totals,
  };
}

export default function RecipesVaultList({
  recipes,
  onCreateOrUpdateRecipe,
  onDeleteRecipe,
}: RecipesVaultListProps) {
  const dispatch = useDispatch();

  // dark mode (global partners slice)
  const darkMode = !!useSelector((state: RootState) => state.partners.darkMode);

  // global text colors
  const baseTextColor = darkMode ? "#f9fafb" : "#111827";
  const subtleTextColor = darkMode
    ? "rgba(249,250,251,0.7)"
    : "rgba(17,24,39,0.7)";

  // drawer theme tokens for readability
  const drawerBg = darkMode ? "#0f0f0f" : "#fff";
  const drawerBorder = darkMode
    ? "1px solid rgba(255,255,255,0.07)"
    : "1px solid rgba(0,0,0,0.06)";
  const drawerTextColor = baseTextColor;
  const drawerSubTextColor = subtleTextColor;
  const drawerFaintBg = darkMode
    ? "rgba(255,255,255,0.02)"
    : "rgba(0,0,0,0.02)";

  // logged-in trainer id from Redux
  const loggedInUserId = useSelector(
    (state: RootState) => state.partners.partnerData?.partnerData?.userId
  );

  // build a fresh empty recipe "template" USING the real userId from Redux
  const buildEmptyRecipe = useCallback((): Recipe => {
    const newId = uid();
    return {
      userId: loggedInUserId || "",
      recipeId: newId,
      createdByTrainerId: loggedInUserId || "",
      title: "New Recipe",
      description: "",
      servingsBase: 1,
      ingredients: [],
      steps: [],
      totalMacros: {
        calories: 0,
        protein: 0,
        carbs: 0,
        fats: 0,
      },
      perServingMacros: {
        calories: 0,
        protein: 0,
        carbs: 0,
        fats: 0,
      },
      labels: [],
    } as Recipe & { createdAt?: string | number };
  }, [loggedInUserId]);

  // view mode + sorting + search + tag filters
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");
  const [sortBy, setSortBy] = useState<SortKey>("title");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");
  const [q, setQ] = useState("");
  const [selectedTags, setSelectedTags] = useState<string[]>([]);

  // all labels (tags) across recipes
  const allTags = React.useMemo(
    () =>
      Array.from(
        new Set(
          recipes.flatMap((r) => (r.labels && r.labels.length ? r.labels : []))
        )
      ).sort((a, b) => a.localeCompare(b)),
    [recipes]
  );

  // Drawer state
  const [panelOpen, setPanelOpen] = useState(false);
  const [mode, setMode] = useState<"CREATE" | "EDIT">("CREATE");
  const [draft, setDraft] = useState<Recipe>(buildEmptyRecipe);

  // saving state for Save button spinner
  const [isSaving, setIsSaving] = useState<boolean>(false);

  // Snackbar (bottom toast) state
  const [toastOpen, setToastOpen] = useState(false);
  const [toastMsg, setToastMsg] = useState<string>("");

  // Confirmation dialog for discarding when exiting editor
  const [confirmDiscardOpen, setConfirmDiscardOpen] = useState(false);

  // expansion state for ingredients and steps
  const [expandedIngredientIdx, setExpandedIngredientIdx] = useState<
    number | null
  >(null);
  const [expandedStepIdx, setExpandedStepIdx] = useState<number | null>(null);

  // Refs to focus step textareas
  const stepRefs = useRef<(HTMLTextAreaElement | null)[]>([]);
  const [pendingFocusStepIdx, setPendingFocusStepIdx] = useState<number | null>(
    null
  );

  useEffect(() => {
    if (pendingFocusStepIdx !== null && stepRefs.current[pendingFocusStepIdx]) {
      const el = stepRefs.current[pendingFocusStepIdx]!;
      // Ensure expanded so it's visible
      setExpandedStepIdx(pendingFocusStepIdx);
      // Focus after a tick to ensure layout complete
      setTimeout(() => {
        el.focus();
        const val = el.value || "";
        el.setSelectionRange(val.length, val.length);
      }, 0);
      setPendingFocusStepIdx(null);
    }
  }, [pendingFocusStepIdx, draft.steps.length]);

  const summarizeRecipe = (r: Recipe) => {
    const protein = r.totalMacros?.protein ?? 0;
    const cals = r.totalMacros?.calories ?? 0;
    const carbs = r.totalMacros?.carbs ?? 0;
    const fats = r.totalMacros?.fats ?? 0;
    return { protein, cals, carbs, fats };
  };

  // filter by search + tag filters
  const filteredRecipes = React.useMemo(() => {
    const t = normalize(q);
    return recipes.filter((r) => {
      const labels = r.labels || [];

      // tag filter
      if (
        selectedTags.length > 0 &&
        !labels.some((lbl) => selectedTags.includes(lbl))
      ) {
        return false;
      }

      if (!t) return true;

      const summary = summarizeRecipe(r);
      const hay = normalize(
        [
          r.title,
          r.description,
          ...labels,
          summary.protein ? `${summary.protein} protein` : "",
          summary.cals ? `${summary.cals} cal` : "",
        ]
          .filter(Boolean)
          .join(" ")
      );
      return hay.includes(t);
    });
  }, [recipes, q, selectedTags]);

  // sort for list view
  const sortedForList = React.useMemo(() => {
    const list = [...filteredRecipes];
    return list.sort((a, b) => {
      let aVal = "";
      let bVal = "";

      switch (sortBy) {
        case "title":
        default:
          aVal = (a.title ?? "").toLowerCase();
          bVal = (b.title ?? "").toLowerCase();
          break;
      }

      const cmp = aVal.localeCompare(bVal);
      return sortDirection === "asc" ? cmp : -cmp;
    });
  }, [filteredRecipes, sortBy, sortDirection]);

  const handleSort = (key: SortKey) => {
    if (sortBy === key) {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(key);
      setSortDirection("asc");
    }
  };

  const toggleTagFilter = (tag: string) => {
    setSelectedTags((prev) =>
      prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]
    );
  };

  // =========================
  // DRAWER HELPERS
  // =========================
  const openWithRecipe = (r?: Recipe) => {
    if (r) {
      // EDIT MODE
      const normalizedIngredients = (r.ingredients || []).map((ing) => ({
        ...ing,
        nutritionPerUnit: ing.nutritionPerUnit
          ? { ...(ing.nutritionPerUnit as any) }
          : emptyNutrition(),
      }));

      const normalized: Recipe = {
        ...r,
        ingredients: normalizedIngredients,
      };

      const { totalMacros, perServingMacros } = computeTotals(
        normalized.ingredients || []
      );

      setDraft({
        ...structuredClone(normalized),
        servingsBase: 1,
        totalMacros,
        perServingMacros,
      });
      setMode("EDIT");
    } else {
      // CREATE MODE
      const fresh = buildEmptyRecipe();
      const { totalMacros, perServingMacros } = computeTotals(
        fresh.ingredients
      );
      setDraft({
        ...fresh,
        servingsBase: 1,
        totalMacros,
        perServingMacros,
      });
      setMode("CREATE");
    }
    setExpandedIngredientIdx(null);
    setExpandedStepIdx(null);
    setPanelOpen(true);
  };

  const actuallyCloseDrawer = () => {
    setPanelOpen(false);
    // Reset create form so a fresh one is shown next time
    setDraft(buildEmptyRecipe());
    setExpandedIngredientIdx(null);
    setExpandedStepIdx(null);
  };

  // Unified close handler that prompts when trying to exit editor
  const requestCloseDrawer = () => {
    setConfirmDiscardOpen(true);
  };

  const handleConfirmDiscard = () => {
    setConfirmDiscardOpen(false);
    actuallyCloseDrawer();
  };

  const handleCancelDiscard = () => {
    setConfirmDiscardOpen(false);
  };

  const quickDeleteRecipe = (recipeId: string) => {
    // optimistic local delete in parent
    onDeleteRecipe(recipeId);

    // thunk to persist delete
    dispatch(deleteRecipe({ recipeId }) as any);

    setToastMsg("Recipe deleted");
    setToastOpen(true);
  };

  const handleSave = async () => {
    if (!draft.title?.trim()) return;

    setIsSaving(true);
    try {
      const finalUserId = loggedInUserId || draft.userId || "";
      const finalTrainerId =
        draft.createdByTrainerId || loggedInUserId || draft.userId || "";

      const toSave: Recipe = {
        ...draft,
        userId: finalUserId,
        createdByTrainerId: finalTrainerId,
      };

      // optimistic local update to parent list
      onCreateOrUpdateRecipe(toSave);

      // kick off thunk to persist
      await dispatch(
        upsertRecipe({
          recipe: toSave,
        }) as any
      );

      setToastMsg("Recipe saved");
      setToastOpen(true);
      setPanelOpen(false);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (mode === "EDIT") {
      const recipeId = draft.recipeId;
      quickDeleteRecipe(recipeId);
    }
    setPanelOpen(false);
  };

  // ---------- draft field setters ----------
  const handleTitleChange = (val: string) => {
    setDraft((d) => ({ ...d, title: val }));
  };

  const handleDescriptionChange = (val: string) => {
    setDraft((d) => ({ ...d, description: val }));
  };

  // keeping for potential manual macro edits later if we ever expose it
  const handleMacroChange = (
    macroType: "perServingMacros" | "totalMacros",
    field: "calories" | "protein" | "carbs" | "fats",
    value: number
  ) => {
    setDraft((d) => ({
      ...d,
      [macroType]: {
        ...d[macroType],
        [field]: value,
      },
    }));
  };

  const addIngredient = () => {
    setDraft((d) => {
      const nextIngredients = [...d.ingredients, emptyIngredient()];
      const { totalMacros, perServingMacros } = computeTotals(nextIngredients);
      return {
        ...d,
        servingsBase: 1,
        ingredients: nextIngredients,
        totalMacros,
        perServingMacros,
      };
    });
    setExpandedIngredientIdx(draft.ingredients.length); // open the new one
  };

  const updateIngredient = (
    idx: number,
    key:
      | "name"
      | "quantity"
      | "unit"
      | "notes"
      | "nutritionPerUnit.calories"
      | "nutritionPerUnit.protein"
      | "nutritionPerUnit.carbs"
      | "nutritionPerUnit.fats",
    value: any
  ) => {
    setDraft((d) => {
      const nextIngredients = [...d.ingredients];
      const ing = { ...nextIngredients[idx] };

      if (key.startsWith("nutritionPerUnit.")) {
        const field = key.split(".")[1] as
          | "calories"
          | "protein"
          | "carbs"
          | "fats";

        const currentNu = ing.nutritionPerUnit
          ? { ...(ing.nutritionPerUnit as any) }
          : emptyNutrition();

        const updatedNu = {
          calories: currentNu.calories ?? 0,
          protein: currentNu.protein ?? 0,
          carbs: currentNu.carbs ?? 0,
          fats: currentNu.fats ?? 0,
          [field]: Number(value),
        };

        ing.nutritionPerUnit = {
          calories: updatedNu.calories,
          protein: updatedNu.protein,
          carbs: updatedNu.carbs,
          fats: updatedNu.fats,
        };
      } else if (key === "quantity") {
        ing.quantity = Number(value); // stored, not used for totals
      } else {
        (ing as any)[key] = value;
      }

      nextIngredients[idx] = ing;

      const { totalMacros, perServingMacros } = computeTotals(nextIngredients);

      return {
        ...d,
        servingsBase: 1,
        ingredients: nextIngredients,
        totalMacros,
        perServingMacros,
      };
    });
  };

  const removeIngredient = (idx: number) => {
    setDraft((d) => {
      const nextIngredients = [...d.ingredients];
      nextIngredients.splice(idx, 1);

      const { totalMacros, perServingMacros } = computeTotals(nextIngredients);

      return {
        ...d,
        servingsBase: 1,
        ingredients: nextIngredients,
        totalMacros,
        perServingMacros,
      };
    });

    if (expandedIngredientIdx === idx) {
      setExpandedIngredientIdx(null);
    }
  };

  const updateStep = (idx: number, value: string) => {
    setDraft((d) => {
      const next = [...d.steps];
      next[idx] = value;
      return { ...d, steps: next };
    });
  };

  const addStep = () => {
    setDraft((d) => ({ ...d, steps: [...d.steps, ""] }));
    const newIdx = draft.steps.length;
    setExpandedStepIdx(newIdx);
    setPendingFocusStepIdx(newIdx);
  };

  const removeStep = (idx: number) => {
    setDraft((d) => {
      const next = [...d.steps];
      next.splice(idx, 1);
      return { ...d, steps: next };
    });
    if (expandedStepIdx === idx) {
      setExpandedStepIdx(null);
    }
  };

  // calories for that ingredient (from the user's inputs, not quantity-based)
  const getIngredientDerivedCalories = (ing: Ingredient) => {
    const protein = ing.nutritionPerUnit?.protein ?? 0;
    const carbs = ing.nutritionPerUnit?.carbs ?? 0;
    const fats = ing.nutritionPerUnit?.fats ?? 0;
    const cals = protein * 4 + carbs * 4 + fats * 9;
    return Math.round(cals);
  };

  // collapsed ingredient summary row
  const renderIngredientSummary = (ing: Ingredient) => {
    const cals = getIngredientDerivedCalories(ing);
    const p = ing.nutritionPerUnit?.protein ?? 0;
    const c = ing.nutritionPerUnit?.carbs ?? 0;
    const f = ing.nutritionPerUnit?.fats ?? 0;

    return (
      <Stack
        direction="row"
        flexWrap="wrap"
        spacing={1}
        sx={{
          fontSize: "0.75rem",
          color: drawerSubTextColor,
        }}
      >
        <Chip
          size="small"
          label={`${ing.quantity}${ing.unit || ""}`}
          sx={{
            height: 20,
            fontSize: "0.7rem",
            fontWeight: 600,
            borderRadius: "999px",
            color: drawerTextColor,
            borderColor: darkMode ? "rgba(255,255,255,0.4)" : "rgba(0,0,0,0.4)",
          }}
          variant="outlined"
        />
        <Chip
          size="small"
          label={`${p}P/${c}C/${f}F`}
          sx={{
            height: 20,
            fontSize: "0.7rem",
            fontWeight: 600,
            borderRadius: "999px",
            color: drawerTextColor,
            borderColor: darkMode ? "rgba(255,255,255,0.4)" : "rgba(0,0,0,0.4)",
          }}
          variant="outlined"
        />
        <Chip
          size="small"
          label={`${cals} cal`}
          sx={{
            height: 20,
            fontSize: "0.7rem",
            fontWeight: 600,
            borderRadius: "999px",
            color: drawerTextColor,
            borderColor: darkMode ? "rgba(255,255,255,0.4)" : "rgba(0,0,0,0.4)",
          }}
          variant="outlined"
        />
      </Stack>
    );
  };

  // collapsed step summary
  const renderStepSummary = (text: string) => {
    const preview =
      text.trim().length > 0
        ? text.trim().slice(0, 60) + (text.trim().length > 60 ? "..." : "")
        : "(empty)";
    return (
      <Typography
        variant="body2"
        sx={{
          fontSize: "0.8rem",
          fontWeight: 400,
          lineHeight: 1.4,
          maxWidth: "100%",
          wordBreak: "break-word",
          color: drawerSubTextColor,
        }}
      >
        {preview}
      </Typography>
    );
  };

  // ENTER on step textarea -> create next step if last isn't empty, then focus it
  const handleStepKeyDown = (e: KeyboardEvent<HTMLDivElement>, idx: number) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      let shouldAdd = false;
      setDraft((d) => {
        const currentText = d.steps[idx]?.trim() ?? "";
        const isLast = idx === d.steps.length - 1;
        if (!isLast || currentText.length === 0) return d;
        shouldAdd = true;
        return { ...d, steps: [...d.steps, ""] };
      });
      if (shouldAdd) {
        const newIdx = idx + 1;
        setExpandedStepIdx(newIdx);
        setPendingFocusStepIdx(newIdx);
      }
    }
  };

  // ---------- render ----------
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        overflow: "hidden",
      }}
    >
      {/* ── Unified toolbar ── */}
      <Stack
        direction="row"
        spacing={1.5}
        mb={2}
        alignItems="center"
        flexWrap="wrap"
        sx={{ rowGap: 1.5 }}
      >
        {/* Search */}
        <TextField
          value={q}
          onChange={(e) => setQ(e.target.value)}
          sx={{
            flex: 1,
            minWidth: 200,
            maxWidth: { xs: "100%", sm: "38%" },
            "& .MuiInputBase-input": { color: baseTextColor },
          }}
          placeholder="Search recipes…"
          size="small"
          label="Search"
          InputLabelProps={{
            sx: {
              color: baseTextColor,
              "&.Mui-focused": { color: baseTextColor },
            },
          }}
        />

        {/* Tags dropdown (multi-select) */}
        {allTags.length > 0 && (
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel
              sx={{
                color: baseTextColor,
                "&.Mui-focused": { color: baseTextColor },
              }}
            >
              Labels
            </InputLabel>
            <Select
              multiple
              value={selectedTags}
              label="Labels"
              onChange={(e) => {
                const val = e.target.value;
                setSelectedTags(typeof val === "string" ? val.split(",") : val);
              }}
              renderValue={(sel) =>
                (sel as string[]).length === 0
                  ? "All"
                  : (sel as string[]).join(", ")
              }
              sx={{
                "& .MuiInputBase-input": { color: baseTextColor },
                "& .MuiSvgIcon-root": { color: subtleTextColor },
              }}
            >
              {allTags.map((tag) => (
                <MenuItem key={tag} value={tag} sx={{ textTransform: "capitalize" }}>
                  {tag}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {/* Spacer */}
        <Box sx={{ flex: "1 0 0", display: { xs: "none", md: "block" } }} />

        {/* View toggles */}
        <Stack direction="row" spacing={0.5} alignItems="center">
          {viewMode === "list" ? (
            <GlassContainer blur={18} radius={999} sx={{ p: 0.25 }} dontHoverPop>
              <IconButton size="small" onClick={() => setViewMode("list")} color="primary">
                <ViewListIcon fontSize="small" />
              </IconButton>
            </GlassContainer>
          ) : (
            <IconButton
              size="small"
              onClick={() => setViewMode("list")}
              color="default"
              sx={{ color: darkMode ? "rgba(249,250,251,0.65)" : "#111827" }}
            >
              <ViewListIcon fontSize="small" />
            </IconButton>
          )}

          {viewMode === "grid" ? (
            <GlassContainer blur={18} radius={999} sx={{ p: 0.25 }} dontHoverPop>
              <IconButton size="small" onClick={() => setViewMode("grid")} color="primary">
                <ViewModuleIcon fontSize="small" />
              </IconButton>
            </GlassContainer>
          ) : (
            <IconButton
              size="small"
              onClick={() => setViewMode("grid")}
              color="default"
              sx={{ color: darkMode ? "rgba(249,250,251,0.65)" : "#111827" }}
            >
              <ViewModuleIcon fontSize="small" />
            </IconButton>
          )}
        </Stack>

        {/* + New Recipe */}
        <GlassContainer
          blur={18}
          radius={999}
          sx={{ pl: 1.25, pr: 1.75, py: 0.4 }}
          dontHoverPop
        >
          <Button
            variant="text"
            startIcon={<AddIcon />}
            onClick={() => openWithRecipe()}
            disabled={!loggedInUserId}
            title={!loggedInUserId ? "No trainer account loaded" : undefined}
            sx={{
              textTransform: "none",
              px: 0,
              minWidth: 0,
              color: baseTextColor,
            }}
          >
            New Recipe
          </Button>
        </GlassContainer>
      </Stack>

      {filteredRecipes.length === 0 ? (
        <Typography variant="body2" sx={{ mt: 0, color: subtleTextColor }}>
          No recipes found. Try creating one or adjusting your filters.
        </Typography>
      ) : viewMode === "list" ? (
        // LIST VIEW
        <GlassContainer
          blur={18}
          radius={14}
          sx={{
            mt: 0,
            overflow: "hidden",
            flex: 1,
          }}
        >
          <TableContainer
            sx={{
              maxHeight: 600,
              backgroundColor: "transparent",
            }}
          >
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow
                  sx={{
                    "& th": {
                      borderBottom: "1px solid",
                      borderBottomColor: darkMode
                        ? "rgba(255,255,255,0.12)"
                        : "rgba(0,0,0,0.06)",
                    },
                  }}
                >
                  <TableCell
                    sx={{
                      backgroundColor: darkMode
                        ? "rgba(255,255,255,0.04)"
                        : "grey.50",
                      color: baseTextColor,
                      fontWeight: 500,
                    }}
                  >
                    <TableSortLabel
                      active={sortBy === "title"}
                      direction={sortBy === "title" ? sortDirection : "asc"}
                      onClick={() => handleSort("title")}
                      sx={{
                        "&.Mui-active": {
                          color: `${baseTextColor} !important`,
                        },
                        "& .MuiTableSortLabel-icon": {
                          color: `${baseTextColor} !important`,
                        },
                      }}
                    >
                      Title
                    </TableSortLabel>
                  </TableCell>
                  <TableCell
                    sx={{
                      backgroundColor: darkMode
                        ? "rgba(255,255,255,0.04)"
                        : "grey.50",
                      color: baseTextColor,
                      fontWeight: 500,
                    }}
                  >
                    Macros
                  </TableCell>
                  <TableCell
                    sx={{
                      backgroundColor: darkMode
                        ? "rgba(255,255,255,0.04)"
                        : "grey.50",
                      color: baseTextColor,
                      fontWeight: 500,
                    }}
                  >
                    Labels
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      backgroundColor: darkMode
                        ? "rgba(255,255,255,0.04)"
                        : "grey.50",
                      color: baseTextColor,
                      fontWeight: 500,
                    }}
                  >
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedForList.map((r) => {
                  const { protein, cals } = summarizeRecipe(r);

                  return (
                    <TableRow
                      key={r.recipeId}
                      hover
                      onClick={() => openWithRecipe(r)}
                      sx={{
                        cursor: "pointer",
                        "& .MuiTableCell-root": {
                          py: 1.25,
                          borderBottom: "1px solid",
                          borderBottomColor: darkMode
                            ? "rgba(255,255,255,0.12)"
                            : "rgba(0,0,0,0.06)",
                        },
                      }}
                    >
                      <TableCell
                        sx={{
                          color: baseTextColor,
                          fontWeight: 500,
                        }}
                      >
                        {r.title || "Untitled Recipe"}
                      </TableCell>
                      <TableCell
                        sx={{
                          color: subtleTextColor,
                        }}
                      >
                        {protein}g protein • {cals} cal
                      </TableCell>
                      <TableCell
                        sx={{
                          color: subtleTextColor,
                        }}
                      >
                        {r.labels && r.labels.length > 0 ? (
                          <Stack direction="row" spacing={0.5} flexWrap="wrap">
                            {r.labels.map((label) => {
                              const colors = getTagColors(label);
                              return (
                                <Chip
                                  key={label}
                                  label={label}
                                  size="small"
                                  variant="outlined"
                                  sx={{
                                    borderRadius: 999,
                                    fontSize: "0.7rem",
                                    ...colors,
                                  }}
                                />
                              );
                            })}
                          </Stack>
                        ) : (
                          <Typography
                            sx={{
                              fontSize: "0.7rem",
                              fontStyle: "italic",
                              color: subtleTextColor,
                            }}
                          >
                            No labels
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          onClick={(ev) => {
                            ev.stopPropagation();
                            quickDeleteRecipe(r.recipeId);
                          }}
                          color="error"
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </GlassContainer>
      ) : (
        // GRID VIEW
        <Box
          sx={{
            flex: 1,
            overflowY: "auto",
            px: 0.5,
            py: 1,
          }}
        >
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
              gap: 1.25,
            }}
          >
            {filteredRecipes.map((r) => {
              const { protein, cals, carbs, fats } = summarizeRecipe(r);
              return (
                <VaultGridCard
                  key={r.recipeId}
                  onClick={() => openWithRecipe(r)}
                  minHeight={176}
                  sx={{ color: baseTextColor }}
                  title={r.title || "Untitled Recipe"}
                  chips={
                    <>
                      <Chip
                        size="small"
                        label={`${cals} cal`}
                        variant="outlined"
                        sx={{
                          borderColor: darkMode ? "rgba(255,255,255,0.4)" : "rgba(0,0,0,0.25)",
                          color: baseTextColor,
                          "& .MuiChip-label": {
                            fontWeight: 600,
                            fontSize: "0.72rem",
                          },
                        }}
                      />
                      <Chip
                        size="small"
                        label={`${protein}P • ${carbs}C • ${fats}F`}
                        variant="outlined"
                        sx={{
                          borderColor: darkMode ? "rgba(255,255,255,0.4)" : "rgba(0,0,0,0.25)",
                          color: subtleTextColor,
                          "& .MuiChip-label": {
                            fontWeight: 500,
                            fontSize: "0.7rem",
                          },
                        }}
                      />
                    </>
                  }
                  body={
                    r.labels && r.labels.length > 0 ? (
                      <Stack direction="row" spacing={0.5} flexWrap="wrap">
                        {r.labels.map((label) => {
                          const colors = getTagColors(label);
                          return (
                            <Chip
                              key={label}
                              size="small"
                              label={label}
                              variant="outlined"
                              sx={{
                                "& .MuiChip-label": {
                                  fontWeight: 500,
                                  fontSize: "0.7rem",
                                },
                                borderRadius: 999,
                                ...colors,
                              }}
                            />
                          );
                        })}
                      </Stack>
                    ) : (
                      <Typography
                        sx={{
                          fontSize: "0.7rem",
                          fontStyle: "italic",
                          color: subtleTextColor,
                        }}
                      >
                        No labels
                      </Typography>
                    )
                  }
                  footer={
                    <Typography
                      sx={{
                        fontSize: "0.68rem",
                        color: subtleTextColor,
                        fontStyle: "italic",
                      }}
                    >
                      Tap to edit recipe
                    </Typography>
                  }
                />
              );
            })}
          </Box>
        </Box>
      )}

      {/* EDITOR DRAWER */}
      <Drawer
        anchor="right"
        open={panelOpen}
        onClose={requestCloseDrawer}
        PaperProps={{
          sx: {
            width: { xs: "100%", sm: 500, md: 600, lg: 680 },
            backgroundColor: drawerBg,
            color: drawerTextColor,
            borderLeft: drawerBorder,
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        {/* HEADER */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            px: 2,
            py: 1.5,
            borderBottom: drawerBorder,
            color: drawerTextColor,
          }}
        >
          <Typography
            sx={{
              flex: 1,
              fontWeight: 600,
              fontSize: "1rem",
              lineHeight: 1.2,
              pr: 1,
              color: drawerTextColor,
            }}
          >
            {mode === "CREATE" ? "New Recipe" : draft.title || "Edit Recipe"}
          </Typography>

          {mode === "EDIT" && (
            <IconButton
              size="small"
              onClick={handleDelete}
              sx={{
                color: drawerTextColor,
                mr: 1,
              }}
              disabled={isSaving}
            >
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          )}

          <IconButton
            size="small"
            onClick={requestCloseDrawer}
            sx={{ color: drawerTextColor }}
            disabled={isSaving}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* BODY */}
        <Box
          sx={{
            flex: 1,
            overflowY: "auto",
            px: 2,
            py: 2,
            display: "flex",
            flexDirection: "column",
            gap: 2.5,
            color: drawerTextColor,
          }}
        >
          {/* SECTION: BASIC INFO */}
          <Box>
            <Typography
              sx={{
                fontSize: "0.75rem",
                fontWeight: 600,
                textTransform: "uppercase",
                letterSpacing: 0.4,
                mb: 1,
                color: drawerSubTextColor,
              }}
            >
              Details
            </Typography>

            <Stack spacing={1.5}>
              <TextField
                label="Recipe title"
                value={draft.title}
                onChange={(e) => handleTitleChange(e.target.value)}
                size="small"
                InputLabelProps={{
                  sx: {
                    color: `${drawerTextColor} !important`,
                    fontSize: "0.75rem",
                    fontWeight: 500,
                  },
                }}
                inputProps={{
                  style: {
                    fontSize: "0.9rem",
                    fontWeight: 600,
                    color: drawerTextColor,
                  },
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "10px",
                    color: drawerTextColor,
                    "& fieldset": {
                      borderColor: darkMode
                        ? "rgba(255,255,255,0.3)"
                        : "rgba(0,0,0,0.3)",
                    },
                    "&:hover fieldset": {
                      borderColor: darkMode
                        ? "rgba(255,255,255,0.6)"
                        : "rgba(0,0,0,0.6)",
                    },
                  },
                }}
              />
              <TextField
                label="Description / Notes"
                multiline
                minRows={2}
                value={draft.description ?? ""}
                onChange={(e) => handleDescriptionChange(e.target.value)}
                size="small"
                InputLabelProps={{
                  sx: {
                    color: `${drawerTextColor} !important`,
                    fontSize: "0.75rem",
                    fontWeight: 500,
                  },
                }}
                inputProps={{
                  style: {
                    fontSize: "0.8rem",
                    lineHeight: 1.4,
                    fontWeight: 400,
                    color: drawerTextColor,
                  },
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "10px",
                    color: drawerTextColor,
                    "& fieldset": {
                      borderColor: darkMode
                        ? "rgba(255,255,255,0.3)"
                        : "rgba(0,0,0,0.3)",
                    },
                    "&:hover fieldset": {
                      borderColor: darkMode
                        ? "rgba(255,255,255,0.6)"
                        : "rgba(0,0,0,0.6)",
                    },
                  },
                }}
              />
            </Stack>
          </Box>

          {/* SECTION: LABELS (tags) */}
          <Box>
            <Typography
              sx={{
                fontSize: "0.75rem",
                fontWeight: 600,
                textTransform: "uppercase",
                letterSpacing: 0.4,
                mb: 1,
                color: drawerSubTextColor,
              }}
            >
              Labels
            </Typography>
            <Autocomplete
              multiple
              freeSolo
              size="small"
              options={allTags}
              value={draft.labels || []}
              onChange={(_e, value) =>
                setDraft((d) => ({
                  ...d,
                  labels: (value as string[])
                    .map((v) => v.trim())
                    .filter(Boolean),
                }))
              }
              renderTags={(value, getTagProps) =>
                value.map((option, index) => {
                  const tagProps = getTagProps({ index });
                  const colors = getTagColors(option);
                  return (
                    <Chip
                      {...tagProps}
                      key={option + index}
                      label={option}
                      size="small"
                      sx={{
                        borderRadius: 999,
                        fontSize: "0.75rem",
                        ...colors,
                        "& .MuiChip-deleteIcon": {
                          color: colors.color,
                        },
                      }}
                    />
                  );
                })
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Add / select labels"
                  placeholder="Type and press Enter to create, or pick existing"
                  InputLabelProps={{
                    sx: {
                      color: `${drawerTextColor} !important`,
                      fontSize: "0.75rem",
                      fontWeight: 500,
                    },
                  }}
                  inputProps={{
                    ...params.inputProps,
                    style: {
                      fontSize: "0.85rem",
                      color: drawerTextColor,
                    },
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "10px",
                      color: drawerTextColor,
                      "& fieldset": {
                        borderColor: darkMode
                          ? "rgba(255,255,255,0.3)"
                          : "rgba(0,0,0,0.3)",
                      },
                      "&:hover fieldset": {
                        borderColor: darkMode
                          ? "rgba(255,255,255,0.6)"
                          : "rgba(0,0,0,0.6)",
                      },
                    },
                  }}
                />
              )}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "10px",
                  color: drawerTextColor,
                  "& fieldset": {
                    borderColor: darkMode
                      ? "rgba(255,255,255,0.3)"
                      : "rgba(0,0,0,0.3)",
                  },
                  "&:hover fieldset": {
                    borderColor: darkMode
                      ? "rgba(255,255,255,0.6)"
                      : "rgba(0,0,0,0.6)",
                  },
                },
              }}
            />
          </Box>

          {/* SECTION: MACROS */}
          <Box>
            <Typography
              sx={{
                fontSize: "0.75rem",
                fontWeight: 600,
                textTransform: "uppercase",
                letterSpacing: 0.4,
                mb: 1,
                color: drawerSubTextColor,
              }}
            >
              Macros
            </Typography>

            <Paper
              elevation={0}
              sx={{
                p: 1.5,
                borderRadius: 2,
                border: drawerBorder,
                backgroundColor: drawerFaintBg,
                color: drawerTextColor,
              }}
            >
              <Typography
                sx={{
                  fontWeight: 600,
                  fontSize: "0.8rem",
                  lineHeight: 1.3,
                  color: drawerTextColor,
                }}
              >
                Entire recipe = 1 serving
              </Typography>

              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1.5}
                flexWrap="wrap"
                sx={{ mt: 1.25 }}
              >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    sx={{
                      fontSize: "0.7rem",
                      lineHeight: 1.3,
                      fontWeight: 500,
                      color: drawerSubTextColor,
                    }}
                  >
                    Calories
                  </Typography>
                  <Typography
                    sx={{
                      fontWeight: 700,
                      fontSize: "0.9rem",
                      lineHeight: 1.4,
                      color: drawerTextColor,
                    }}
                  >
                    {draft.totalMacros?.calories ?? 0} cal
                  </Typography>
                </Box>

                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    sx={{
                      fontSize: "0.7rem",
                      lineHeight: 1.3,
                      fontWeight: 500,
                      color: drawerSubTextColor,
                    }}
                  >
                    Protein
                  </Typography>
                  <Typography
                    sx={{
                      fontWeight: 700,
                      fontSize: "0.9rem",
                      lineHeight: 1.4,
                      color: drawerTextColor,
                    }}
                  >
                    {draft.totalMacros?.protein ?? 0} g
                  </Typography>
                </Box>

                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    sx={{
                      fontSize: "0.7rem",
                      lineHeight: 1.3,
                      fontWeight: 500,
                      color: drawerSubTextColor,
                    }}
                  >
                    Carbs
                  </Typography>
                  <Typography
                    sx={{
                      fontWeight: 700,
                      fontSize: "0.9rem",
                      lineHeight: 1.4,
                      color: drawerTextColor,
                    }}
                  >
                    {draft.totalMacros?.carbs ?? 0} g
                  </Typography>
                </Box>

                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    sx={{
                      fontSize: "0.7rem",
                      lineHeight: 1.3,
                      fontWeight: 500,
                      color: drawerSubTextColor,
                    }}
                  >
                    Fats
                  </Typography>
                  <Typography
                    sx={{
                      fontWeight: 700,
                      fontSize: "0.9rem",
                      lineHeight: 1.4,
                      color: drawerTextColor,
                    }}
                  >
                    {draft.totalMacros?.fats ?? 0} g
                  </Typography>
                </Box>
              </Stack>
            </Paper>
          </Box>

          {/* SECTION: INGREDIENTS */}
          <Box>
            <Stack
              direction="row"
              alignItems="baseline"
              justifyContent="space-between"
              sx={{ mb: 1 }}
            >
              <Typography
                sx={{
                  fontSize: "0.75rem",
                  fontWeight: 600,
                  textTransform: "uppercase",
                  letterSpacing: 0.4,
                  color: drawerSubTextColor,
                }}
              >
                Ingredients
              </Typography>
              <Button
                size="small"
                variant="outlined"
                sx={{
                  textTransform: "none",
                  borderRadius: "8px",
                  fontWeight: 600,
                  fontSize: "0.7rem",
                  lineHeight: 1.2,
                  px: 1,
                  py: 0.5,
                  color: drawerTextColor,
                  borderColor: darkMode
                    ? "rgba(255,255,255,0.4)"
                    : "rgba(0,0,0,0.4)",
                  "&:hover": {
                    borderColor: darkMode
                      ? "rgba(255,255,255,0.7)"
                      : "rgba(0,0,0,0.7)",
                    backgroundColor: darkMode
                      ? "rgba(255,255,255,0.06)"
                      : "rgba(0,0,0,0.04)",
                  },
                }}
                onClick={addIngredient}
              >
                + Add ingredient
              </Button>
            </Stack>

            {draft.ingredients.length === 0 ? (
              <Typography
                sx={{
                  fontSize: "0.8rem",
                  lineHeight: 1.4,
                  fontStyle: "italic",
                  fontWeight: 400,
                  color: drawerSubTextColor,
                }}
              >
                No ingredients yet
              </Typography>
            ) : (
              draft.ingredients.map((ing, idx) => {
                const isExpanded = expandedIngredientIdx === idx;
                const derivedCals = getIngredientDerivedCalories(ing);

                return (
                  <Paper
                    key={idx}
                    elevation={0}
                    sx={{
                      p: 1.25,
                      mb: 1,
                      borderRadius: 2,
                      border: drawerBorder,
                      backgroundColor: drawerFaintBg,
                      cursor: "pointer",
                      color: drawerTextColor,
                    }}
                  >
                    <Stack spacing={1}>
                      {/* HEADER ROW */}
                      <Stack
                        direction="row"
                        alignItems="flex-start"
                        justifyContent="space-between"
                        onClick={() =>
                          setExpandedIngredientIdx(isExpanded ? null : idx)
                        }
                      >
                        <Box sx={{ maxWidth: "80%" }}>
                          {/* Ingredient name */}
                          <Typography
                            sx={{
                              fontWeight: 600,
                              fontSize: "0.8rem",
                              lineHeight: 1.3,
                              wordBreak: "break-word",
                              color: drawerTextColor,
                            }}
                          >
                            {ing.name || "New ingredient"}
                          </Typography>

                          {!isExpanded && (
                            <Box sx={{ mt: 0.5 }}>
                              {renderIngredientSummary(ing)}
                            </Box>
                          )}
                        </Box>

                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            removeIngredient(idx);
                          }}
                          sx={{ color: drawerTextColor }}
                        >
                          <DeleteOutlineIcon
                            sx={{ fontSize: "1rem", opacity: 0.7 }}
                          />
                        </IconButton>
                      </Stack>

                      {isExpanded && (
                        <>
                          <Divider
                            sx={{
                              borderColor: drawerBorder.split(" ")[2],
                            }}
                          />

                          {/* BASIC FIELDS */}
                          <Stack direction="row" spacing={1}>
                            <TextField
                              label="Name"
                              size="small"
                              value={ing.name}
                              onChange={(e) =>
                                updateIngredient(idx, "name", e.target.value)
                              }
                              InputLabelProps={{
                                sx: {
                                  color: `${drawerTextColor} !important`,
                                  fontSize: "0.7rem",
                                  fontWeight: 500,
                                },
                              }}
                              inputProps={{
                                style: {
                                  fontSize: "0.8rem",
                                  fontWeight: 600,
                                  color: drawerTextColor,
                                },
                              }}
                              FormHelperTextProps={{
                                sx: { color: drawerSubTextColor },
                              }}
                              sx={{
                                flex: 2,
                                minWidth: 0,
                                "& .MuiOutlinedInput-root": {
                                  borderRadius: "10px",
                                  color: drawerTextColor,
                                  "& fieldset": {
                                    borderColor: darkMode
                                      ? "rgba(255,255,255,0.3)"
                                      : "rgba(0,0,0,0.3)",
                                  },
                                  "&:hover fieldset": {
                                    borderColor: darkMode
                                      ? "rgba(255,255,255,0.6)"
                                      : "rgba(0,0,0,0.6)",
                                  },
                                },
                              }}
                            />
                            <TextField
                              label="Qty"
                              size="small"
                              type="number"
                              value={ing.quantity}
                              onChange={(e) =>
                                updateIngredient(
                                  idx,
                                  "quantity",
                                  e.target.value
                                )
                              }
                              InputLabelProps={{
                                sx: {
                                  color: `${drawerTextColor} !important`,
                                  fontSize: "0.7rem",
                                  fontWeight: 500,
                                },
                              }}
                              inputProps={{
                                style: {
                                  fontSize: "0.8rem",
                                  fontWeight: 500,
                                  color: drawerTextColor,
                                },
                              }}
                              sx={{
                                width: 80,
                                "& .MuiOutlinedInput-root": {
                                  borderRadius: "10px",
                                  color: drawerTextColor,
                                  "& fieldset": {
                                    borderColor: darkMode
                                      ? "rgba(255,255,255,0.3)"
                                      : "rgba(0,0,0,0.3)",
                                  },
                                  "&:hover fieldset": {
                                    borderColor: darkMode
                                      ? "rgba(255,255,255,0.6)"
                                      : "rgba(0,0,0,0.6)",
                                  },
                                },
                              }}
                            />
                            <TextField
                              label="Unit"
                              size="small"
                              value={ing.unit}
                              onChange={(e) =>
                                updateIngredient(idx, "unit", e.target.value)
                              }
                              InputLabelProps={{
                                sx: {
                                  color: `${drawerTextColor} !important`,
                                  fontSize: "0.7rem",
                                  fontWeight: 500,
                                },
                              }}
                              inputProps={{
                                style: {
                                  fontSize: "0.8rem",
                                  fontWeight: 500,
                                  color: drawerTextColor,
                                },
                              }}
                              sx={{
                                width: 80,
                                "& .MuiOutlinedInput-root": {
                                  borderRadius: "10px",
                                  color: drawerTextColor,
                                  "& fieldset": {
                                    borderColor: darkMode
                                      ? "rgba(255,255,255,0.3)"
                                      : "rgba(0,0,0,0.3)",
                                  },
                                  "&:hover fieldset": {
                                    borderColor: darkMode
                                      ? "rgba(255,255,255,0.6)"
                                      : "rgba(0,0,0,0.6)",
                                  },
                                },
                              }}
                            />
                          </Stack>

                          <TextField
                            label="Notes"
                            size="small"
                            value={ing.notes ?? ""}
                            onChange={(e) =>
                              updateIngredient(idx, "notes", e.target.value)
                            }
                            InputLabelProps={{
                              sx: {
                                color: `${drawerTextColor} !important`,
                                fontSize: "0.7rem",
                                fontWeight: 500,
                              },
                            }}
                            inputProps={{
                              style: {
                                fontSize: "0.8rem",
                                lineHeight: 1.4,
                                color: drawerTextColor,
                              },
                            }}
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                borderRadius: "10px",
                                color: drawerTextColor,
                                "& fieldset": {
                                  borderColor: darkMode
                                    ? "rgba(255,255,255,0.3)"
                                    : "rgba(0,0,0,0.3)",
                                },
                                "&:hover fieldset": {
                                  borderColor: darkMode
                                    ? "rgba(255,255,255,0.6)"
                                    : "rgba(0,0,0,0.6)",
                                },
                              },
                            }}
                          />

                          {/* MACROS INLINE GRID */}
                          <Stack
                            direction={{ xs: "column", sm: "row" }}
                            spacing={1}
                            flexWrap="wrap"
                          >
                            <TextField
                              label="Protein"
                              size="small"
                              type="number"
                              value={ing.nutritionPerUnit?.protein ?? 0}
                              onChange={(e) =>
                                updateIngredient(
                                  idx,
                                  "nutritionPerUnit.protein",
                                  e.target.value
                                )
                              }
                              InputLabelProps={{
                                sx: {
                                  color: `${drawerTextColor} !important`,
                                  fontSize: "0.7rem",
                                  fontWeight: 500,
                                },
                              }}
                              inputProps={{
                                style: {
                                  fontSize: "0.8rem",
                                  fontWeight: 500,
                                  color: drawerTextColor,
                                },
                              }}
                              sx={{
                                flex: 1,
                                minWidth: 0,
                                "& .MuiOutlinedInput-root": {
                                  borderRadius: "10px",
                                  color: drawerTextColor,
                                  "& fieldset": {
                                    borderColor: darkMode
                                      ? "rgba(255,255,255,0.3)"
                                      : "rgba(0,0,0,0.3)",
                                  },
                                  "&:hover fieldset": {
                                    borderColor: darkMode
                                      ? "rgba(255,255,255,0.6)"
                                      : "rgba(0,0,0,0.6)",
                                  },
                                },
                              }}
                            />
                            <TextField
                              label="Carbs"
                              size="small"
                              type="number"
                              value={ing.nutritionPerUnit?.carbs ?? 0}
                              onChange={(e) =>
                                updateIngredient(
                                  idx,
                                  "nutritionPerUnit.carbs",
                                  e.target.value
                                )
                              }
                              InputLabelProps={{
                                sx: {
                                  color: `${drawerTextColor} !important`,
                                  fontSize: "0.7rem",
                                  fontWeight: 500,
                                },
                              }}
                              inputProps={{
                                style: {
                                  fontSize: "0.8rem",
                                  fontWeight: 500,
                                  color: drawerTextColor,
                                },
                              }}
                              sx={{
                                flex: 1,
                                minWidth: 0,
                                "& .MuiOutlinedInput-root": {
                                  borderRadius: "10px",
                                  color: drawerTextColor,
                                  "& fieldset": {
                                    borderColor: darkMode
                                      ? "rgba(255,255,255,0.3)"
                                      : "rgba(0,0,0,0.3)",
                                  },
                                  "&:hover fieldset": {
                                    borderColor: darkMode
                                      ? "rgba(255,255,255,0.6)"
                                      : "rgba(0,0,0,0.6)",
                                  },
                                },
                              }}
                            />
                            <TextField
                              label="Fats"
                              size="small"
                              type="number"
                              value={ing.nutritionPerUnit?.fats ?? 0}
                              onChange={(e) =>
                                updateIngredient(
                                  idx,
                                  "nutritionPerUnit.fats",
                                  e.target.value
                                )
                              }
                              InputLabelProps={{
                                sx: {
                                  color: `${drawerTextColor} !important`,
                                  fontSize: "0.7rem",
                                  fontWeight: 500,
                                },
                              }}
                              inputProps={{
                                style: {
                                  fontSize: "0.8rem",
                                  fontWeight: 500,
                                  color: drawerTextColor,
                                },
                              }}
                              sx={{
                                flex: 1,
                                minWidth: 0,
                                "& .MuiOutlinedInput-root": {
                                  borderRadius: "10px",
                                  color: drawerTextColor,
                                  "& fieldset": {
                                    borderColor: darkMode
                                      ? "rgba(255,255,255,0.3)"
                                      : "rgba(0,0,0,0.3)",
                                  },
                                  "&:hover fieldset": {
                                    borderColor: darkMode
                                      ? "rgba(255,255,255,0.6)"
                                      : "rgba(0,0,0,0.6)",
                                  },
                                },
                              }}
                            />

                            {/* Calories (read-only text) */}
                            <Box
                              sx={{
                                flex: 1,
                                minWidth: 0,
                                display: "flex",
                                flexDirection: "column",
                                justifyContent: "center",
                              }}
                            >
                              <Typography
                                sx={{
                                  fontSize: "0.7rem",
                                  lineHeight: 1.3,
                                  fontWeight: 500,
                                  color: drawerSubTextColor,
                                }}
                              >
                                Calories
                              </Typography>
                              <Typography
                                sx={{
                                  fontWeight: 700,
                                  fontSize: "0.9rem",
                                  lineHeight: 1.4,
                                  color: drawerTextColor,
                                }}
                              >
                                {derivedCals} cal
                              </Typography>
                            </Box>
                          </Stack>
                        </>
                      )}
                    </Stack>
                  </Paper>
                );
              })
            )}
          </Box>

          {/* SECTION: STEPS */}
          <Box>
            <Stack
              direction="row"
              alignItems="baseline"
              justifyContent="space-between"
              sx={{ mb: 1 }}
            >
              <Typography
                sx={{
                  fontSize: "0.75rem",
                  fontWeight: 600,
                  textTransform: "uppercase",
                  letterSpacing: 0.4,
                  color: drawerSubTextColor,
                }}
              >
                Steps
              </Typography>
              <Button
                size="small"
                variant="outlined"
                sx={{
                  textTransform: "none",
                  borderRadius: "8px",
                  fontWeight: 600,
                  fontSize: "0.7rem",
                  lineHeight: 1.2,
                  px: 1,
                  py: 0.5,
                  color: drawerTextColor,
                  borderColor: darkMode
                    ? "rgba(255,255,255,0.4)"
                    : "rgba(0,0,0,0.4)",
                  "&:hover": {
                    borderColor: darkMode
                      ? "rgba(255,255,255,0.7)"
                      : "rgba(0,0,0,0.7)",
                    backgroundColor: darkMode
                      ? "rgba(255,255,255,0.06)"
                      : "rgba(0,0,0,0.04)",
                  },
                }}
                onClick={addStep}
              >
                + Add step
              </Button>
            </Stack>

            {draft.steps.length === 0 ? (
              <Typography
                sx={{
                  fontSize: "0.8rem",
                  lineHeight: 1.4,
                  fontStyle: "italic",
                  fontWeight: 400,
                  color: drawerSubTextColor,
                }}
              >
                No steps yet
              </Typography>
            ) : (
              draft.steps.map((text, idx) => {
                const isExpanded = expandedStepIdx === idx;
                return (
                  <Paper
                    key={idx}
                    elevation={0}
                    sx={{
                      p: 1.25,
                      mb: 1,
                      borderRadius: 2,
                      border: drawerBorder,
                      backgroundColor: drawerFaintBg,
                      cursor: "pointer",
                      color: drawerTextColor,
                    }}
                  >
                    <Stack spacing={1}>
                      {/* HEADER ROW */}
                      <Stack
                        direction="row"
                        alignItems="flex-start"
                        justifyContent="space-between"
                        onClick={() =>
                          setExpandedStepIdx(isExpanded ? null : idx)
                        }
                      >
                        <Box sx={{ maxWidth: "80%" }}>
                          <Typography
                            sx={{
                              fontWeight: 600,
                              fontSize: "0.8rem",
                              lineHeight: 1.3,
                              color: drawerTextColor,
                            }}
                          >
                            Step {idx + 1}
                          </Typography>

                          {!isExpanded && (
                            <Box sx={{ mt: 0.5 }}>
                              {renderStepSummary(text)}
                            </Box>
                          )}
                        </Box>

                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            removeStep(idx);
                          }}
                          sx={{ color: drawerTextColor }}
                        >
                          <DeleteOutlineIcon
                            sx={{ fontSize: "1rem", opacity: 0.7 }}
                          />
                        </IconButton>
                      </Stack>

                      {isExpanded && (
                        <>
                          <Divider
                            sx={{
                              borderColor: drawerBorder.split(" ")[2],
                            }}
                          />

                          <TextField
                            label={`Step ${idx + 1} instructions`}
                            multiline
                            minRows={3}
                            value={text}
                            onChange={(e) => updateStep(idx, e.target.value)}
                            onKeyDown={(e) => handleStepKeyDown(e, idx)}
                            size="small"
                            inputRef={(el) => {
                              stepRefs.current[idx] = el;
                            }}
                            InputLabelProps={{
                              sx: {
                                color: `${drawerTextColor} !important`,
                                fontSize: "0.7rem",
                                fontWeight: 500,
                              },
                            }}
                            inputProps={{
                              style: {
                                fontSize: "0.8rem",
                                lineHeight: 1.4,
                                color: drawerTextColor,
                              },
                            }}
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                borderRadius: "10px",
                                color: drawerTextColor,
                                "& fieldset": {
                                  borderColor: darkMode
                                    ? "rgba(255,255,255,0.3)"
                                    : "rgba(0,0,0,0.3)",
                                },
                                "&:hover fieldset": {
                                  borderColor: darkMode
                                    ? "rgba(255,255,255,0.6)"
                                    : "rgba(0,0,0,0.6)",
                                },
                              },
                            }}
                          />
                        </>
                      )}
                    </Stack>
                  </Paper>
                );
              })
            )}
          </Box>
        </Box>

        {/* FOOTER */}
        <Box
          sx={{
            p: 2,
            borderTop: drawerBorder,
            display: "flex",
            gap: 1,
            flexWrap: "wrap",
            justifyContent: "flex-end",
            color: drawerTextColor,
          }}
        >
          <Button
            onClick={requestCloseDrawer}
            sx={{
              textTransform: "none",
              borderRadius: "10px",
              fontWeight: 500,
              fontSize: "0.8rem",
              lineHeight: 1.3,
              px: 1.5,
              py: 0.75,
              color: drawerTextColor,
              "&:hover": {
                backgroundColor: darkMode
                  ? "rgba(255,255,255,0.06)"
                  : "rgba(0,0,0,0.04)",
              },
            }}
            disabled={isSaving}
          >
            Cancel
          </Button>
          <Button
            color="primary"
            variant="contained"
            onClick={handleSave}
            disabled={!draft.title?.trim() || isSaving}
            sx={{
              textTransform: "none",
              borderRadius: "10px",
              fontWeight: 600,
              fontSize: "0.8rem",
              lineHeight: 1.3,
              px: 1.5,
              py: 0.75,
              display: "inline-flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            {isSaving && (
              <CircularProgress size={16} thickness={4} color="inherit" />
            )}
            Save
          </Button>
        </Box>
      </Drawer>

      {/* DISCARD CONFIRMATION (for exiting editor) */}
      <Dialog
        open={confirmDiscardOpen}
        onClose={handleCancelDiscard}
        aria-labelledby="discard-dialog-title"
        aria-describedby="discard-dialog-description"
        PaperProps={{
          sx: {
            backgroundColor: drawerBg,
            color: drawerTextColor,
            border: drawerBorder,
          },
        }}
      >
        <DialogTitle id="discard-dialog-title" sx={{ fontWeight: 700 }}>
          {mode === "CREATE" ? "Discard new recipe?" : "Discard changes?"}
        </DialogTitle>
        <DialogContent id="discard-dialog-description">
          <Typography
            sx={{
              fontSize: "0.9rem",
              lineHeight: 1.5,
              color: drawerSubTextColor,
            }}
          >
            Are you sure you want to close? All unsaved data for this recipe
            will be lost.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={handleCancelDiscard}
            sx={{
              textTransform: "none",
              borderRadius: "10px",
              fontWeight: 500,
              fontSize: "0.85rem",
            }}
          >
            Keep Editing
          </Button>
          <Button
            onClick={handleConfirmDiscard}
            color="error"
            variant="contained"
            sx={{
              textTransform: "none",
              borderRadius: "10px",
              fontWeight: 600,
              fontSize: "0.85rem",
            }}
          >
            Discard
          </Button>
        </DialogActions>
      </Dialog>

      {/* BOTTOM TOAST FOR SAVE/DELETE */}
      <Snackbar
        open={toastOpen}
        autoHideDuration={3000}
        onClose={() => setToastOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setToastOpen(false)}
          severity="success"
          elevation={3}
          variant="filled"
          sx={{ borderRadius: 2 }}
        >
          {toastMsg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
